import Computer from "@mui/icons-material/Computer";
import Edit from "@mui/icons-material/Edit";
import MoreVert from "@mui/icons-material/MoreVert";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Person from "@mui/icons-material/Person";
import Refresh from "@mui/icons-material/Refresh";
import {
  Box,
  IconButton,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { ManifestDocumentsQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  DocumentReviewStatus,
  FILTERABLE_DOCUMENT_REVIEW_STATUSES,
  getDocumentReviewStatus,
  getDocumentReviewStatusLabel,
  getDocumentReviewStatusLabelFromRow,
} from "util/documentReviewStatus";

import { ManifestReviewDialog } from "../../Customer/Waste/Manifests/ManifestReviewDialog";
import { MANIFEST_DOCUMENTS_QUERY } from "./api";
import { buildManifestDocumentsSearch, documentSourceKind, reparseMenuLabel } from "./helpers";
import { ReParseDialog } from "./ReParseDialog";

type ManifestDocumentsProps = {
  tenantId: string;
};

type Row = ManifestDocumentsQuery["documents"]["items"][number];

const REVIEW_STATUS_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  FILTERABLE_DOCUMENT_REVIEW_STATUSES.map((status) => [
    status,
    getDocumentReviewStatusLabel(status),
  ])
);

// The "Source" column is backed by the derived `documentSource` omnisearch key
// (api | human) rather than the raw documentType, so filter + sort stay
// consistent with the icon after a printed copy is parsed (which moves the
// printed-copy type into originalDocumentType). The resolver resolves the key
// against both documentType and originalDocumentType.
const SOURCE_FILTER_VALUES: string[] = ["api", "human"];
const SOURCE_FILTER_LABELS: Record<string, string> = {
  api: "API (RCRAInfo)",
  human: "Human upload",
};

/**
 * Staff-only diagnostic page that lists every RcraManifest document in the
 * tenant alongside the manifest record it ended up linked to (if any). Helps
 * identify orphaned documents and documents that landed on the wrong manifest.
 */
export function ManifestDocuments({ tenantId }: ManifestDocumentsProps) {
  const theme = useTheme();

  // Row to re-parse. Null when the dialog is closed. The dialog itself owns
  // the override field state; this is just the open/closed signal + which row.
  const [pendingReParseRow, setPendingReParseRow] = useState<Row | null>(null);
  // Documents to open in the manifest-specific review dialog. Used both for
  // direct edits (Edit action) and for the post-re-parse review handoff —
  // the analyzing dialog otherwise falls back to the generic Document AI flow.
  const [reviewDocumentIds, setReviewDocumentIds] = useState<string[] | null>(null);
  // Per-row overflow menu anchor (Re-parse lives here as a secondary action).
  // Two states instead of an object so each updates with one setter call.
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [actionsAnchorRow, setActionsAnchorRow] = useState<Row | null>(null);
  const closeActionsMenu = useCallback(() => {
    setActionsAnchorEl(null);
    setActionsAnchorRow(null);
  }, []);

  const columns = useMemo<OmnisearchGridColDef<Row>[]>(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: 2,
        minWidth: 200,
        renderCell: ({ row }) =>
          tenantId ? (
            <MuiLink
              href={`/o/${tenantId}/documents/${row.id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <Typography variant="body2" noWrap>
                {row.title}
              </Typography>
              <OpenInNew fontSize="inherit" />
            </MuiLink>
          ) : (
            <Typography variant="body2">{row.title}</Typography>
          ),
      },
      {
        field: "documentSource",
        headerName: "Source",
        flex: 0,
        minWidth: 90,
        filterKeyType: "enumAutocomplete",
        enumValues: SOURCE_FILTER_VALUES,
        enumPresentationFunction: (value) => SOURCE_FILTER_LABELS[value] ?? "Human upload",
        renderCell: ({ row }) =>
          documentSourceKind(row.documentType, row.originalDocumentType) === "api" ? (
            <Tooltip title="Ingested from RCRAInfo (API)">
              <Computer fontSize="small" sx={{ color: "text.secondary" }} />
            </Tooltip>
          ) : (
            <Tooltip title="Uploaded by a user">
              <Person fontSize="small" sx={{ color: "text.secondary" }} />
            </Tooltip>
          ),
      },
      {
        field: "createdAt",
        headerName: "Uploaded",
        flex: 0.7,
        minWidth: 130,
        valueGetter: (_, row) => row.createdAt,
        renderCell: ({ row }) => (row.createdAt ? getUtcDisplayDate(row.createdAt, "med") : "—"),
      },
      {
        field: "reviewStatus",
        headerName: "Review Status",
        flex: 0.7,
        minWidth: 140,
        filterKeyType: "enumAutocomplete",
        enumValues: FILTERABLE_DOCUMENT_REVIEW_STATUSES,
        enumPresentationFunction: (enumValue) =>
          REVIEW_STATUS_LABEL_BY_VALUE[enumValue] ?? enumValue,
        valueGetter: (_, row) => getDocumentReviewStatusLabelFromRow(row),
        renderCell: ({ row }) => {
          const label = getDocumentReviewStatusLabelFromRow(row);
          const errorTitle =
            getDocumentReviewStatus(row) === DocumentReviewStatus.Failed
              ? (row.extractionError ?? "")
              : "";
          return (
            <Tooltip title={errorTitle}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" noWrap>
                  {label}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "lastExtractionAt",
        headerName: "Last parsed",
        flex: 0.7,
        minWidth: 130,
        filterKeyType: "date",
        valueGetter: (_, row) => row.lastExtractionAt,
        renderCell: ({ row }) =>
          row.lastExtractionAt ? getUtcDisplayDate(row.lastExtractionAt, "med") : "—",
      },
      {
        field: "trackingNumber",
        headerName: "Tracking #",
        flex: 0.8,
        minWidth: 150,
        sortable: false,
        valueGetter: (_, row) => row.sourceWasteManifest?.manifestTrackingNumber ?? "",
        renderCell: ({ row }) => {
          const mtn = row.sourceWasteManifest?.manifestTrackingNumber;
          return mtn ? (
            <Typography variant="body2">{mtn}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "extractedGeneratorId",
        headerName: "Generator ID",
        flex: 0.7,
        minWidth: 140,
        sortable: false,
        valueGetter: (_, row) => row.sourceWasteManifest?.generatorId ?? "",
        renderCell: ({ row }) => {
          const id = row.sourceWasteManifest?.generatorId;
          return id ? (
            <Typography variant="body2">{id}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "shippedDate",
        headerName: "Shipped Date",
        flex: 0.7,
        minWidth: 120,
        sortable: false,
        valueGetter: (_, row) => row.sourceWasteManifest?.shippedDate ?? "",
        renderCell: ({ row }) =>
          row.sourceWasteManifest?.shippedDate
            ? getUtcDisplayDate(row.sourceWasteManifest.shippedDate, "med")
            : "—",
      },
      {
        field: "linkedFacility",
        headerName: "Linked facility",
        flex: 0.9,
        minWidth: 160,
        sortable: false,
        valueGetter: (_, row) => row.sourceProcessedManifest?.facility?.name ?? "",
        renderCell: ({ row }) => {
          const facility = row.sourceProcessedManifest?.facility;
          return facility ? (
            <Typography variant="body2" noWrap>
              {facility.name}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "linkedManifest",
        headerName: "Linked manifest",
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => row.sourceProcessedManifest?.manifestTrackingNumber ?? "",
        renderCell: ({ row }) => {
          const linked = row.sourceProcessedManifest;
          if (!linked || !tenantId) {
            return (
              <Typography
                variant="body2"
                color={theme.palette.warning.main}
                sx={{ fontStyle: "italic" }}
              >
                Not linked
              </Typography>
            );
          }
          return (
            <MuiLink
              component={RouterLink}
              to={`/o/${tenantId}/waste/manifests/${linked.id}`}
              variant="body2"
            >
              {linked.manifestTrackingNumber}
            </MuiLink>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        flex: 0,
        minWidth: 96,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit manifest fields">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setReviewDocumentIds([row.id])}
                  aria-label="Edit manifest fields"
                  disabled={!row.sourceWasteManifest?.id}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="More actions">
              <IconButton
                size="small"
                onClick={(e) => {
                  setActionsAnchorEl(e.currentTarget);
                  setActionsAnchorRow(row);
                }}
                aria-label="More actions"
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [tenantId, theme]
  );

  const defaultSearch = useMemo(() => buildManifestDocumentsSearch(tenantId), [tenantId]);

  return (
    <>
      <Box sx={{ pb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Lists every RCRA manifest document in this tenant — including EPA printed-copy scans that
          have not been parsed yet — and the manifest record each ended up linked to. Use this to
          spot orphaned uploads, documents that landed on the wrong manifest, or printed copies that
          still need parsing.
        </Typography>
      </Box>

      <OmnisearchDataGrid
        persistenceKey="waste-manifest-documents"
        isURLDriven={false}
        columns={columns}
        dataQuery={MANIFEST_DOCUMENTS_QUERY}
        getItems={(data) => data.documents.items}
        getCount={(data) => data.documents.count}
        defaultSearch={defaultSearch}
        initialSortModel={[{ field: "createdAt", sort: "desc" }]}
        noDataMessage="No manifest documents in this tenant."
        rowHeight="auto"
        withPadding={false}
        // trackingNumber/extractedGeneratorId/linkedManifest/linkedFacility and the
        // lastExtractionAt date filter are now wired to the resolver (ENG-15798).
        // shippedDate (not a doc-health signal) and actions (UI artifact) stay
        // suppressed — they remain dead auto-generated filters.
        excludeFilterColumns={["shippedDate", "actions"]}
      />

      <Menu anchorEl={actionsAnchorEl} open={Boolean(actionsAnchorEl)} onClose={closeActionsMenu}>
        <MenuItem
          onClick={() => {
            const row = actionsAnchorRow;
            closeActionsMenu();
            if (row) setPendingReParseRow(row);
          }}
        >
          <ListItemIcon>
            <Refresh fontSize="small" />
          </ListItemIcon>
          <ListItemText>{reparseMenuLabel(actionsAnchorRow?.extractionStatus)}</ListItemText>
        </MenuItem>
      </Menu>

      {pendingReParseRow && (
        <ReParseDialog
          key={pendingReParseRow.id}
          row={pendingReParseRow}
          tenantId={tenantId}
          onClose={() => setPendingReParseRow(null)}
          onReadyForReview={setReviewDocumentIds}
        />
      )}

      {reviewDocumentIds && (
        <ManifestReviewDialog
          onClose={() => setReviewDocumentIds(null)}
          documentIds={reviewDocumentIds}
        />
      )}
    </>
  );
}
